import { IconBranchFill, IconProjectFill } from '@saucelabs/ds-icons';
import { Typography } from '@saucelabs/ds-typography';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

import { thunks } from '../../ducks/filterBar';
import { track } from '../../global/tracking';
import { graphql, useFragment } from '../../graphql/__generated__';
import { BuildStatus, BuildsViewBuildFragmentFragment } from '../../graphql/__generated__/graphql';
import { useVisualDispatch } from '../../reduxHooks';
import { urls } from '../../routes';
import ConditionalWrapper from '../common/ConditionalWrapper';
import DateLabel from '../common/DateLabel';
import ProgressBar from '../common/ProgressBar';
import Truncate from '../common/Truncate';
import VisualStatus from '../common/VisualStatus';
import { getVisualProgressData } from '../common/utils';
import Styles from './BuildsItem.styles';
import BuildsItemMenu from './BuildsItemMenu';

export const BuildsViewBuildFragment = graphql(`
  fragment BuildsViewBuildFragment on Build {
    id
    name
    status
    branch
    createdByUser {
      id,
      username
    }
    createdAt
    project
    approved: diffCountExtended(input: { status: APPROVED})
    approvedNew: diffCountExtended(input: { status: APPROVED, withBaseline: false })
    equal: diffCountExtended(input: { status: EQUAL})
    equalNew: diffCountExtended(input: { status: EQUAL, withBaseline: false })
    rejected: diffCountExtended(input: { status: REJECTED})
    rejectedNew: diffCountExtended(input: { status: REJECTED, withBaseline: false })
    unapproved: diffCountExtended(input: { status: UNAPPROVED})
    unapprovedNew: diffCountExtended(input: { status: UNAPPROVED, withBaseline: false })
  }
`);

export type BuildsItemProps = {
  build: BuildsViewBuildFragmentFragment;
  isClickable?: boolean;
  onBulkApprove?: () => void;
}

const VIEWABLE_STATUSES = [
  BuildStatus.Approved,
  BuildStatus.Unapproved,
  BuildStatus.Equal,
  BuildStatus.Rejected,
  BuildStatus.Running,
  BuildStatus.Errored,
];

export const BuildsItem: React.FC<
  BuildsItemProps
> = ({ build, onBulkApprove, isClickable = true }) => {
  const data = useFragment(BuildsViewBuildFragment, build);
  const progressBarData = useMemo(() => getVisualProgressData(data), [data]);
  const projectRef = useRef<HTMLDivElement>(null);
  const isRunning = data.status === BuildStatus.Running;
  const isFinishedAndNotEmpty = !isRunning &&
    data.status !== BuildStatus.Empty;
  const dispatch = useVisualDispatch();

  const onUserClick = (event: React.MouseEvent) => {
    event.preventDefault();
    void dispatch(thunks.selectOwnerByUserId(data.createdByUser.id));
  };

  const isViewable = VIEWABLE_STATUSES.includes(data.status);

  return (
    <div className={Styles.item}>
      <div className="border-0 border-b border-gray-200 border-solid">
        <ConditionalWrapper
          condition={isViewable && isClickable}
          wrapper={(content) => (
            <Link
              className="block hover:no-underline hover:bg-gray-50"
              onClick={track.buildClicked}
              to={urls.getDiff(build.id)}
            >
              <>{content}</>
            </Link>
          )}
        >
          <div className="flex flex-grow items-center gap-2 mx-2 py-2 overflow-hidden">
            <div className="flex flex-grow gap-2 items-center flex-shrink min-w-0 flex-wrap md:flex-nowrap">
              <div className="w-16 flex-shrink-0">
                <VisualStatus buildType={data.status} />
              </div>
              <div className="flex flex-1 flex-col md:flex-row w-full md:w-4/5 min-w-0 gap-1 md:gap-2">
                <div className="flex flex-col w-full md:w-1/2">
                  <Truncate
                    className={classNames('font-semibold', { 'text-high-emphasis hover:underline': isViewable && isClickable, 'text-medium-emphasis': !isViewable })}
                    text={data.name}
                    variant="body-large"
                  />
                  <div className="flex items-center gap-0.5 self-start text-medium-emphasis truncate max-w-full">
                    <DateLabel data={data.createdAt} />
                    {data.createdByUser?.username && (
                      <>
                        <Typography
                          component="span"
                          variant="body"
                        >
                          by
                        </Typography>
                        <ConditionalWrapper
                          condition={isClickable}
                          wrapper={(children) => (
                            <Link
                              className="text-gray-600 hover:text-blue-700 relative z-10 truncate"
                              onClick={onUserClick}
                              to={urls.getBuilds()}
                            >
                              {children}
                            </Link>
                          )}
                        >
                          {`@${data.createdByUser.username}`}
                        </ConditionalWrapper>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-nowrap w-full md:w-1/2 text-medium-emphasis fill-current">
                  <div
                    ref={projectRef}
                    className="flex items-center gap-1 max-w-19 min-w-0"
                  >
                    {data.project && (
                      <>
                        <IconProjectFill className="h-2 w-2 flex-shrink-0" />
                        <Truncate
                          className="mr-2"
                          text={data.project}
                          variant="body"
                        />
                      </>
                    )}
                  </div>
                  <div
                    className="flex items-center gap-1 min-w-0 flex-1"
                    style={{ maxWidth: `calc(100% - ${Number(projectRef.current?.offsetWidth)}px)` }}
                  >
                    {data.branch && (
                      <>
                        <IconBranchFill className="h-2 w-2 flex-shrink-0" />
                        <Truncate
                          text={data.branch}
                          variant="body"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden xl:flex flex-shrink-0 items-center xl:w-24 justify-end">
              {!isRunning && (
                <ProgressBar
                  data={progressBarData}
                />
              )}
            </div>
            <div className="ml-2 min-w-4">
              {isFinishedAndNotEmpty && (
                <BuildsItemMenu
                  buildId={build.id}
                  noBaseline={progressBarData.approvableNew}
                  onCompleted={onBulkApprove}
                  total={progressBarData.approvable}
                />
              )}
            </div>
          </div>
        </ConditionalWrapper>
      </div>
    </div>

  );
};

export default BuildsItem;
